/**
 * File lsystem.js.
 *
 * Handles rendering and logic of l system project
 */

/* global jQuery */
/* global templateDir */

import * as THREE from 'three';
import * as Stats from 'stats.js';
import * as dat from 'dat.gui';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { WEBGL } from 'three/examples/jsm/WebGL';

jQuery(document).ready(() => {
  if (WEBGL.isWebGLAvailable()) {
    init();
    animate();
  } else {
    const warning = WEBGL.getWebGLErrorMessage();
    document.querySelector('.container').appendChild(warning);
  }
});

let container, stats;
let camera, scene, renderer;
let orbitCamera, controls;
let shaderParams;
let uniforms;
let containerWidth, containerHeight;
const vertexShader = `
  void main()	{
    gl_Position = vec4( position, 1.0 );
  }
`;
const fragmentShader = `
  uniform float time;
	uniform vec2 resolution;
	uniform sampler2D noise_tex;
	uniform float k_a;
	uniform float k_d;
	uniform float k_s;
	uniform float shininess;
	uniform vec3 terrainColor;
	uniform vec3 terrainColor2;
	uniform float terrainHeight;
	uniform float terrainRatio;
	uniform float fogAmount;
	uniform float fogDensity;
	uniform vec3 fogColor;
	uniform vec3 skyColor;
	uniform vec3 sunColor;

	uniform vec3 cEye;
	uniform vec3 cUp;
	uniform vec3 cLook;

	const mat2 m2 = mat2(0.8,-0.6,0.6,0.8);

	// fractional brownian motion
	float fbm( vec2 p ) {
		float f = 0.0;
		f += 0.5000*texture2D( noise_tex, p/256.0 ).x; p = m2*p*2.02;
		f += 0.2500*texture2D( noise_tex, p/256.0 ).x; p = m2*p*2.03;
		f += 0.1250*texture2D( noise_tex, p/256.0 ).x; p = m2*p*2.01;
		f += 0.0625*texture2D( noise_tex, p/256.0 ).x;
		return f/0.9375;
	}

	// value noise, and its analytical derivatives
	vec3 noised( in vec2 x ) {
		vec2 p = floor(x);
		vec2 f = fract(x);
		vec2 u = f*f*(3.0-2.0*f);
		float a = texture2D(noise_tex,(p+vec2(0.5,0.5))/256.0,-100.0).x;
		float b = texture2D(noise_tex,(p+vec2(1.5,0.5))/256.0,-100.0).x;
		float c = texture2D(noise_tex,(p+vec2(0.5,1.5))/256.0,-100.0).x;
		float d = texture2D(noise_tex,(p+vec2(1.5,1.5))/256.0,-100.0).x;
		return vec3(a+(b-a)*u.x+(c-a)*u.y+(a-b-c+d)*u.x*u.y,
					6.0*f*(1.0-f)*(vec2(b-a,c-a)+(a-b-c+d)*u.yx));
	}

	float terrain( in vec2 x ) {
		vec2  p = x*0.003;
		float a = 0.0;
		float b = 1.0;
		vec2  d = vec2(0.0);
		for( int i=0; i<6; i++ )
		{
			vec3 n = noised(p);
			d += n.yz;
			a += b*n.x/(1.0+dot(d,d));
			b *= 0.5;
			p = m2*p*2.0;
		}

		return min( terrainHeight*a,terrainHeight*1.5 );
	}

	float normals( in vec2 x ) {
		vec2  p = x*0.003;
		float a = 0.0;
		float b = 1.0;
		vec2  d = vec2(0.0);
		for( int i=0; i<14; i++ )
		{
			vec3 n = noised(p);
			d += n.yz;
			a += b*n.x/(1.0+dot(d,d));
			b *= 0.5;
			p = m2*p*2.0;
		}

		return terrainHeight*a;
	}

	float map( in vec3 p ) {
		return p.y - terrain(p.xz);
	}

	float intersect( in vec3 ro, in vec3 rd, in float tmin, in float tmax) {
		float t = tmin;
		// raymarch up to 120 iterations
		for (int i=0; i<120; i++) {
			float h = map(ro + t*rd);
			// Check if ray is at intersection
			if (h<(0.002*t) || t>tmax) {
				break;
			}
			t += 0.5*h;
		}
		return t;
	}

	float softShadow(in vec3 ro, in vec3 rd) {
		float res = 1.0;
		float t = 0.001;
		// controls how soft/hard shadows
		float k = 6.0;
		for ( int i=0; i<48; i++ ) {
			// get current raymarch position
			vec3 p = ro + t*rd;
			// get closest distance to object
			float h = map( p );
			// see if
			res = min( res, k*h/t );
			t += h;
			if ( res<0.001 || p.y>200.0 ) {
				break;
			}
		}
		return clamp( res, 0.0, 1.0 );
	}

	vec3 calculateNormal( in vec3 pos, in float t) {
		// how far away to use for central differences
		vec2 eps = vec2(0.002*t, 0.0);
		return normalize( vec3( normals(pos.xz-eps.xy) - normals(pos.xz+eps.xy),
							   2.0*eps.x,
							   normals(pos.xz-eps.yx) - normals(pos.xz+eps.yx) ) );
	}

	vec3 applyFog( in vec3 color, in float t, in vec3 ro, in vec3 rd) {
		float fogMix = fogAmount * exp(-ro.y*fogDensity) * (1.0-exp( -t*rd.y*fogDensity ))/rd.y;
		return mix( color, fogColor, fogMix );
	}

	void main()	{
		vec2 xy = -1.0 + 2.0*gl_FragCoord.xy/resolution.xy;
		//normalize aspect ratio
		vec2 s = xy*vec2(resolution.x/resolution.y,1.0);

		// Setup camera
		vec3 cw = normalize( cLook );
		vec3 cv = normalize( cUp - cw*dot(cUp,cw) );
		vec3 cu = normalize( cross(cw,cv) );
		// get ray origin and direction
		vec3 ro = cEye;
		vec3 rd = normalize( s.x*cu + s.y*cv + 2.0*cw );

		// get extents of raymarch
		float tmin = 2.0;
		float tmax = 2000.0;
		// max height
		float maxh = 1.5*terrainHeight;
		float tp = (maxh-ro.y)/rd.y;
		if( tp>0.0 )
		{
			if( ro.y>maxh ) tmin = max( tmin, tp );
			else            tmax = min( tmax, tp );
		}

		// sun is a directional  light
		vec3 sunLight = normalize( vec3(-0.8,0.4,-0.3) );
		vec3 indirectLight = vec3( -sunLight.x,0.0,-sunLight.z );
		// initialize final color
		vec3 color = vec3(0.0);
		// get intersection
		float t = intersect( ro,rd,tmin,tmax );
		// INTERSECTION FOUND
		if (t<tmax) {
			// calculate intersection point + normal and reflected ray
			vec3 pos = ro + t*rd;
			vec3 normal = calculateNormal( pos,t );
			vec3 ref = reflect( rd, normal );

			// calculate base color
			float rockNoise = smoothstep( 0.0,1.0,fbm(pos.xz) );
			rockNoise = mix( rockNoise,0.0,normal.y );
			color = mix( 0.8*terrainColor,terrainColor,rockNoise );

			// calculate top color based on how horizontal terrain is
			float horzLevel = smoothstep( terrainRatio,0.9,normal.y );
			color = mix( color, terrainColor2, smoothstep( 0.1, 0.9, horzLevel ) );
			color = color/7.0;
			// calculate ambient color
			vec3 ambient = k_a*color;
			// calculate diffuse color
			// add diffuse sunlight and indirect light w/shadows from sun
			float sh = softShadow( pos+sunLight,sunLight );
			vec3 diffuse = clamp(dot(sunLight,normal),0.0,1.0)*sunColor*7.0*sh;
			diffuse += clamp( dot( indirectLight, normal ), 0.0, 1.0 )*vec3(0.40,0.50,0.60);
			diffuse *= k_d*color;
			// calculate specular color
			vec3 specular = pow(clamp(dot(ref,-rd),0.0,1.0),shininess)*k_s*color;
			// calculate final color
			color = ambient + diffuse + specular;

			// fog
			color = applyFog( color, t, ro, rd );

		}
		// NO INTERSECTION FOUND
		else {
			// sky color depends on azimuth angle of ray
			color = skyColor*(1.0-rd.y);
			// clouds
			vec2 sc = ro.xz+ rd.xz*(1000.0-ro.y)/rd.y;
			color = mix( color, vec3(1.0,0.95,1.0), 0.5*smoothstep(0.5,0.8,fbm(0.0005*sc)) );
			// horizon
			vec3 horizonColor = mix( sunColor,fogColor,0.4 );
			color = mix( color, horizonColor, pow( 1.0-max(rd.y,0.0), 8.0 ) );
		}
		// gamma correction
		color = pow(color,vec3(0.4545));
		gl_FragColor = vec4(color,1.0);
	}
`;
const guiSettings = {
  preset: 'Mountains',
  closed: false,
  remembered: {
    Default: {
      '0': {
        k_a: 0.5,
        k_d: 0.95,
        k_s: 0.5,
        shininess: 22,
        terrainColor: [142.8, 89.25, 53.55],
        terrainColor2: [229.5, 242.25, 252.45],
        terrainHeight: 140,
        terrainRatio: 0.5,
        fogAmount: 0.3,
        fogDensity: 0.001,
        fogColor: [127.5, 178.5, 229.5],
        skyColor: [76.5, 140.25, 204],
        sunColor: [252.45, 181.04999999999998, 109.64999999999999]
      }
    },
    Grasslands: {
      '0': {
        k_a: 0.46013513513513515,
        k_d: 0.2914864864864865,
        k_s: 0.21364864864864866,
        shininess: 54.229729729729726,
        terrainColor: [
          3.813046875000001,
          28.049999999999997,
          12.053610937500006
        ],
        terrainColor2: [191.658796875, 232.05, 207.81527812500002],
        terrainHeight: 29.067567567567565,
        terrainRatio: 1,
        fogAmount: 0.07527027027027026,
        fogDensity: 0.07758108108108108,
        fogColor: [216.32590312500002, 226.95000000000002, 173.829515625],
        skyColor: [199.82203218749999, 201.45000000000002, 120.051609375],
        sunColor: [219.29999999999998, 82.44309375, 82.44309375]
      }
    },
    Mountains: {
      '0': {
        k_a: 0.5,
        k_d: 0.95,
        k_s: 0.5,
        shininess: 22,
        terrainColor: [142.8, 89.25, 53.55],
        terrainColor2: [229.5, 242.25, 252.45],
        terrainHeight: 140,
        terrainRatio: 0.5,
        fogAmount: 0.3,
        fogDensity: 0.001,
        fogColor: [127.5, 178.5, 229.5],
        skyColor: [76.5, 140.25, 204],
        sunColor: [252.45, 181.04999999999998, 109.64999999999999]
      }
    },
    Jungle: {
      '0': {
        k_a: 0.6763513513513514,
        k_d: 0.8752702702702703,
        k_s: 0.31310810810810813,
        shininess: 25.689189189189186,
        terrainColor: [
          45.90000000000001,
          25.408518750000002,
          11.747531250000005
        ],
        terrainColor2: [
          20.234250000000003,
          40.80000000000001,
          24.758715000000002
        ],
        terrainHeight: 114.68918918918918,
        terrainRatio: 0.8925675675675676,
        fogAmount: 0.0622972972972973,
        fogDensity: 0.001,
        fogColor: [127.5, 178.5, 229.5],
        skyColor: [26.234718749999995, 110.62849874999995, 158.1],
        sunColor: [160.65, 150.4637859375, 58.787859375000004]
      }
    }
  },
  folders: {
    'Terrain Parameters': {
      preset: 'Default',
      closed: false,
      folders: {}
    },
    'Global Parameters': {
      preset: 'Default',
      closed: false,
      folders: {}
    }
  }
};

function init() {
  container = document.querySelector('.three-container');
  containerWidth = container.offsetWidth;
  containerHeight = container.offsetHeight;

  scene = new THREE.Scene();

  renderer = new THREE.WebGLRenderer();
  container.appendChild(renderer.domElement);

  camera = new THREE.Camera();
  camera.position.z = 1;

  // Use orbit camera
  orbitCamera = new THREE.PerspectiveCamera(
    60.0,
    containerWidth / containerHeight,
    1.0,
    1000
  );
  orbitCamera.position.x = 300.0;
  orbitCamera.position.y = 300.0;
  orbitCamera.position.z = 800.0;
  let lookVector = new THREE.Vector3(0.0, 0.0, -1.0);
  lookVector.applyQuaternion(orbitCamera.quaternion);
  // Setup controls
  controls = new OrbitControls(orbitCamera, container);
  controls.addEventListener('change', render);

  const geometry = new THREE.PlaneBufferGeometry(2, 2);
  const imageFilename = templateDir + '/graphics/noise256.png';
  let noiseImage = THREE.ImageUtils.loadTexture(imageFilename);
  noiseImage.wrapS = THREE.RepeatWrapping;
  noiseImage.wrapT = THREE.RepeatWrapping;
  uniforms = {
    time: { type: 'f', value: 1.0 },
    resolution: { type: 'v2', value: new THREE.Vector2() },
    noise_tex: { type: 't', value: noiseImage },
    k_a: { type: 'f', value: 0.5 },
    k_d: { type: 'f', value: 0.95 },
    k_s: { type: 'f', value: 0.5 },
    shininess: { type: 'f', value: 22.0 },
    terrainColor: { type: 'c', value: new THREE.Color(0.56, 0.35, 0.21) },
    terrainColor2: { type: 'c', value: new THREE.Color(0.9, 0.95, 0.99) },
    terrainHeight: { type: 'f', value: 140.0 },
    terrainRatio: { type: 'f', value: 0.5 },

    fogAmount: { type: 'f', value: 0.3 },
    fogDensity: { type: 'f', value: 0.001 },
    fogColor: { type: 'c', value: new THREE.Color(0.5, 0.7, 0.9) },
    skyColor: { type: 'c', value: new THREE.Color(0.3, 0.55, 0.8) },
    sunColor: { type: 'c', value: new THREE.Color(0.99, 0.71, 0.43) },

    cEye: { type: 'v3', value: orbitCamera.position },
    cUp: { type: 'v3', value: orbitCamera.up },
    cLook: { type: 'v3', value: lookVector }
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader,
    fragmentShader
  });

  const mesh = new THREE.Mesh(geometry, material);
  scene.add(mesh);

  stats = new Stats();
  stats.domElement.style.position = 'absolute';
  stats.domElement.style.top = '0px';
  let statsContainer = document.querySelector('.stats-container');
  statsContainer.appendChild(stats.domElement);

  shaderParams = {
    k_a: uniforms.k_a.value,
    k_d: uniforms.k_d.value,
    k_s: uniforms.k_s.value,
    shininess: uniforms.shininess.value,
    terrainColor: [
      uniforms.terrainColor.value.r * 255.0,
      uniforms.terrainColor.value.g * 255.0,
      uniforms.terrainColor.value.b * 255.0
    ],
    terrainColor2: [
      uniforms.terrainColor2.value.r * 255.0,
      uniforms.terrainColor2.value.g * 255.0,
      uniforms.terrainColor2.value.b * 255.0
    ],
    terrainHeight: uniforms.terrainHeight.value,
    terrainRatio: uniforms.terrainRatio.value,

    fogAmount: uniforms.fogAmount.value,
    fogDensity: uniforms.fogDensity.value,
    fogColor: [
      uniforms.fogColor.value.r * 255.0,
      uniforms.fogColor.value.g * 255.0,
      uniforms.fogColor.value.b * 255.0
    ],
    skyColor: [
      uniforms.skyColor.value.r * 255.0,
      uniforms.skyColor.value.g * 255.0,
      uniforms.skyColor.value.b * 255.0
    ],
    sunColor: [
      uniforms.sunColor.value.r * 255.0,
      uniforms.sunColor.value.g * 255.0,
      uniforms.sunColor.value.b * 255.0
    ]
  };
  const gui = new dat.GUI({ autoPlace: false, load: guiSettings });
  const guiContainer = document.querySelector('.gui-container');
  gui.remember(shaderParams);
  guiContainer.appendChild(gui.domElement);
  let terrainFolder = gui.addFolder('Terrain Parameters');
  terrainFolder
    .add(shaderParams, 'k_a')
    .min(0.0)
    .max(1.0)
    .name('k_a')
    .onChange(onParamsChange);
  terrainFolder
    .add(shaderParams, 'k_d')
    .min(0.0)
    .max(1.0)
    .name('k_d')
    .onChange(onParamsChange);
  terrainFolder
    .add(shaderParams, 'k_s')
    .min(0.0)
    .max(1.0)
    .name('k_s')
    .onChange(onParamsChange);
  terrainFolder
    .add(shaderParams, 'shininess')
    .min(0.0)
    .max(100.0)
    .name('Shininess')
    .onChange(onParamsChange);
  terrainFolder
    .addColor(shaderParams, 'terrainColor')
    .name('Terrain Color')
    .onChange(onParamsChange);
  terrainFolder
    .addColor(shaderParams, 'terrainColor2')
    .name('2nd Terrain Color')
    .onChange(onParamsChange);
  terrainFolder
    .add(shaderParams, 'terrainHeight')
    .min(0.0)
    .max(300.0)
    .name('Terrain Height')
    .onChange(onParamsChange);
  terrainFolder
    .add(shaderParams, 'terrainRatio')
    .min(0.0)
    .max(1.0)
    .name('Terrain Ratio')
    .onChange(onParamsChange);
  terrainFolder.open();

  let globalFolder = gui.addFolder('Global Parameters');
  globalFolder
    .add(shaderParams, 'fogAmount')
    .min(0.0)
    .max(1.0)
    .name('Fog Amount')
    .onChange(onParamsChange);
  globalFolder
    .add(shaderParams, 'fogDensity')
    .min(0.0)
    .max(0.1)
    .name('Fog Density')
    .onChange(onParamsChange);
  globalFolder
    .addColor(shaderParams, 'fogColor')
    .name('Fog Color')
    .onChange(onParamsChange);
  globalFolder
    .addColor(shaderParams, 'skyColor')
    .name('Sky Color')
    .onChange(onParamsChange);
  globalFolder
    .addColor(shaderParams, 'sunColor')
    .name('Sun Color')
    .onChange(onParamsChange);
  globalFolder.open();

  onWindowResize();
  window.addEventListener('resize', onWindowResize, false);
}

function onParamsChange() {
  //copy gui params into shader uniforms
  uniforms.k_a.value = shaderParams.k_a;
  uniforms.k_s.value = shaderParams.k_s;
  uniforms.k_d.value = shaderParams.k_d;
  uniforms.shininess.value = shaderParams.shininess;
  uniforms.terrainColor.value = parseColor(shaderParams.terrainColor);
  uniforms.terrainColor2.value = parseColor(shaderParams.terrainColor2);
  uniforms.terrainHeight.value = shaderParams.terrainHeight;
  uniforms.terrainRatio.value = shaderParams.terrainRatio;

  uniforms.fogAmount.value = shaderParams.fogAmount;
  uniforms.fogDensity.value = shaderParams.fogDensity;
  uniforms.fogColor.value = parseColor(shaderParams.fogColor);
  uniforms.skyColor.value = parseColor(shaderParams.skyColor);
  uniforms.sunColor.value = parseColor(shaderParams.sunColor);
}

function parseColor(colorVal) {
  let myColor = new THREE.Color();
  // if color is a hex string
  if (colorVal.length > 3) {
    myColor.set(colorVal);
  } else if (colorVal.length == 3) {
    myColor.set(
      new THREE.Color(
        colorVal[0] / 255.0,
        colorVal[1] / 255.0,
        colorVal[2] / 255.0
      )
    );
  }
  return myColor;
}

function onWindowResize() {
  containerWidth = container.offsetWidth;
  containerHeight = container.offsetHeight;

  orbitCamera.aspect = containerWidth / containerHeight;
  orbitCamera.updateProjectionMatrix();

  uniforms.resolution.value.x = containerWidth;
  uniforms.resolution.value.y = containerHeight;
  renderer.setSize(containerWidth, containerHeight);
}

function animate() {
  window.requestAnimationFrame(animate);
  render();
  stats.update();
}

function render() {
  uniforms.time.value += 0.05;
  // Change camera uniforms
  uniforms.cEye.value = orbitCamera.position;
  uniforms.cUp.value = orbitCamera.up;
  let lookVector = new THREE.Vector3(0.0, 0.0, -1.0);
  lookVector.applyQuaternion(orbitCamera.quaternion);
  uniforms.cLook.value = lookVector;

  renderer.render(scene, camera);
}
